import { useEffect, useId, useRef, useState } from 'react'

interface PillOption<T> {
  value: T
  label: string
  shortLabel?: string
}

interface PillSelectProps<T> {
  label: string
  value: T
  options: PillOption<T>[]
  onChange: (value: T) => void
  icon?: React.ReactNode
  ariaLabel?: string
}

export default function PillSelect<T extends string | number>({
  label,
  value,
  options,
  onChange,
  icon,
  ariaLabel,
}: PillSelectProps<T>) {
  const [open, setOpen] = useState(false)
  const listId = useId()
  const containerRef = useRef<HTMLDivElement>(null)
  const selected = options.find(option => option.value === value)

  useEffect(() => {
    const handlePointerDown = (event: PointerEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
      }
    }

    document.addEventListener('pointerdown', handlePointerDown)
    document.addEventListener('keydown', handleKeyDown)
    return () => {
      document.removeEventListener('pointerdown', handlePointerDown)
      document.removeEventListener('keydown', handleKeyDown)
    }
  }, [])

  return (
    <div className={`pill-select ${open ? 'pill-select--open' : ''}`} ref={containerRef}>
      <button
        type="button"
        className="pill-select__trigger"
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={listId}
        onClick={() => setOpen(prev => !prev)}
      >
        {icon ? <span className="pill-select__icon" aria-hidden="true">{icon}</span> : null}
        <span className="pill-select__label">{label}</span>
        <span className="pill-select__value">{selected?.shortLabel ?? selected?.label ?? 'Select'}</span>
        <span className="pill-select__chevron" aria-hidden="true">⌄</span>
      </button>
      <div
        id={listId}
        role="listbox"
        className="pill-select__list"
        aria-label={ariaLabel || label}
        hidden={!open}
      >
        {options.map(option => (
          <button
            key={String(option.value)}
            type="button"
            role="option"
            aria-selected={option.value === value}
            className={`pill-select__option ${option.value === value ? 'is-active' : ''}`}
            onClick={() => {
              onChange(option.value)
              setOpen(false)
            }}
          >
            {option.label}
          </button>
        ))}
      </div>
    </div>
  )
}
